import { buildMovementCatalog, fetchMovementBundle, impliedProbability } from './movementData.js'
import { normalizeLeagueCode, normalizeTeamCode } from './oddsNormalization.js'

const BOARD_DATA_BASE_URL = 'https://abet-board-data.abetco.workers.dev'
const SOURCE_TIMEOUT_MS = 20_000
const MARKET_ORDER = ['Moneyline', 'Spread', 'Run Line', 'Puck Line', 'Total']

let provenancePromise = null

async function fetchBoardArtifact(name) {
  const response = await fetch(`${BOARD_DATA_BASE_URL}/data/${name}`, {
    cache: 'no-store',
    signal: AbortSignal.timeout(SOURCE_TIMEOUT_MS),
  })
  if (!response.ok) throw new Error(`${name} unavailable (HTTP ${response.status})`)
  return response.json()
}

function easternDateFromIso(value) {
  const timestamp = Date.parse(value || '')
  if (!Number.isFinite(timestamp)) return ''
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(new Date(timestamp))
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]))
  return `${values.year}-${values.month}-${values.day}`
}

export function leagueFromSportKey(sportKey, fallback = '') {
  const key = String(sportKey || '').toLowerCase()
  if (key.includes('baseball_mlb')) return 'MLB'
  if (key.includes('basketball_wnba')) return 'WNBA'
  if (key.includes('basketball_nba')) return 'NBA'
  if (key.includes('basketball_ncaab') || key.includes('basketball_wncaab')) return 'CBB'
  if (key.includes('americanfootball_ncaaf')) return 'CFB'
  if (key.includes('americanfootball_nfl')) return 'NFL'
  if (key.includes('icehockey_nhl')) return 'NHL'
  if (key.includes('soccer')) return 'SOCCER'
  if (key.includes('tennis')) return 'TENNIS'
  if (key.includes('golf')) return 'GOLF'
  if (key.includes('mma')) return 'MMA'
  if (key.includes('boxing')) return 'BOXING'
  return normalizeLeagueCode(fallback)
}

export function scheduleEventToTarget(event, sportKey) {
  return {
    league: leagueFromSportKey(event?.sport_key || sportKey, event?.sport_title),
    awayTeam: event?.away_team || '',
    homeTeam: event?.home_team || '',
    date: easternDateFromIso(event?.commence_time),
    startTime: event?.commence_time || '',
    sourceEventId: event?.id || '',
  }
}

export function parseBoardEvent(eventName) {
  const text = String(eventName || '').trim()
  const separator = text.includes(' @ ') ? ' @ ' : text.includes(' vs ') ? ' vs ' : null
  if (!separator) return { awayTeam: '', homeTeam: '', event: text }
  const [awayTeam = '', homeTeam = ''] = text.split(separator, 2)
  return { awayTeam, homeTeam, event: `${awayTeam} @ ${homeTeam}` }
}

export function normalizeEventTarget(target) {
  const league = normalizeLeagueCode(target?.league)
  return {
    ...target,
    league,
    awayCode: normalizeTeamCode(target?.awayTeam, league),
    homeCode: normalizeTeamCode(target?.homeTeam, league),
  }
}

function eventIdentity(eventName, league) {
  const teams = parseBoardEvent(eventName)
  const normalizedLeague = normalizeLeagueCode(league)
  return {
    league: normalizedLeague,
    awayCode: normalizeTeamCode(teams.awayTeam, normalizedLeague),
    homeCode: normalizeTeamCode(teams.homeTeam, normalizedLeague),
  }
}

export function matchesEventTarget(row, target) {
  const normalizedTarget = normalizeEventTarget(target)
  const identity = eventIdentity(row?.event, row?.league)
  if (!normalizedTarget.awayCode || !normalizedTarget.homeCode) return false
  if (normalizedTarget.league && identity.league !== normalizedTarget.league) return false
  if (identity.awayCode !== normalizedTarget.awayCode || identity.homeCode !== normalizedTarget.homeCode) return false
  if (normalizedTarget.date && row?.et_date && normalizedTarget.date !== row.et_date) return false
  return true
}

function marketSort(left, right) {
  const leftIndex = MARKET_ORDER.indexOf(left.market)
  const rightIndex = MARKET_ORDER.indexOf(right.market)
  const safeLeft = leftIndex === -1 ? MARKET_ORDER.length : leftIndex
  const safeRight = rightIndex === -1 ? MARKET_ORDER.length : rightIndex
  if (safeLeft !== safeRight) return safeLeft - safeRight
  if (left.market !== right.market) return left.market.localeCompare(right.market)
  if (left.period !== right.period) return left.period.localeCompare(right.period)
  const leftLine = left.line == null ? Number.NEGATIVE_INFINITY : Number(left.line)
  const rightLine = right.line == null ? Number.NEGATIVE_INFINITY : Number(right.line)
  if (leftLine !== rightLine) return leftLine - rightLine
  return left.selection.localeCompare(right.selection)
}

export function buildEventOfferGroups(rows, target) {
  const eventRows = (rows || []).filter((row) => matchesEventTarget(row, target))
  const books = Array.from(new Set(eventRows.map((row) => row.book).filter(Boolean))).sort()
  const groups = new Map()

  eventRows.forEach((row) => {
    const period = row.period || 'Game'
    const line = row.line == null ? null : Number(row.line)
    const key = [period, row.bet_type || 'Unknown', row.selection || 'Unknown', line ?? 'None'].join('|')
    if (!groups.has(key)) {
      groups.set(key, {
        key,
        period,
        market: row.bet_type || 'Unknown',
        selection: row.selection || 'Unknown',
        rawSelection: row.sel_raw || row.selection || 'Unknown',
        line,
        offers: {},
        bestAmerican: null,
      })
    }

    const group = groups.get(key)
    const current = group.offers[row.book]
    if (!current || Number(row.american) > Number(current.american)) group.offers[row.book] = row
    if (Number.isFinite(Number(row.american))) {
      group.bestAmerican = group.bestAmerican == null
        ? Number(row.american)
        : Math.max(group.bestAmerican, Number(row.american))
    }
  })

  return {
    eventRows,
    books,
    groups: Array.from(groups.values()).sort(marketSort),
    markets: Array.from(new Set(eventRows.map((row) => row.bet_type || 'Unknown')))
      .sort((left, right) => marketSort({ market: left, period: '', line: null, selection: '' }, { market: right, period: '', line: null, selection: '' })),
    lineCount: new Set(eventRows.map((row) => [row.period || 'Game', row.bet_type, row.line ?? 'None'].join('|'))).size,
  }
}

function edgeMatchesTarget(entry, target) {
  return matchesEventTarget({ event: entry?.event, league: entry?.league, et_date: entry?.date }, target)
}

function movementDelta(entry) {
  const opening = impliedProbability(entry.openingOdds)
  const current = impliedProbability(entry.currentOdds)
  return opening == null || current == null ? null : (current - opening) * 100
}

export function buildEventIntelligence(bundle, target) {
  const catalog = buildMovementCatalog(bundle?.movement)
  const offers = buildEventOfferGroups(bundle?.movement?.board?.rows || [], target)
  const matchingGame = catalog.games.find((game) => matchesEventTarget({ event: game.event, league: game.league, et_date: game.date }, target)) || null
  const movementSeries = matchingGame
    ? catalog.series
      .filter((entry) => entry.gameId === matchingGame.id)
      .map((entry) => ({ ...entry, deltaProbabilityPoints: movementDelta(entry) }))
      .sort((left, right) => Math.abs(right.deltaProbabilityPoints || 0) - Math.abs(left.deltaProbabilityPoints || 0))
    : []
  const edge = bundle?.edge || {}

  return {
    target: normalizeEventTarget(target),
    eventName: offers.eventRows[0]?.event || `${target?.awayTeam || ''} @ ${target?.homeTeam || ''}`,
    eventDate: offers.eventRows[0]?.et_date || target?.date || '',
    eventTime: offers.eventRows[0]?.et_time || target?.startTime || '',
    ...offers,
    movementGame: matchingGame,
    movementSeries,
    opportunities: {
      evs: (edge.evs || []).filter((entry) => edgeMatchesTarget(entry, target)),
      arbs: (edge.arbs || []).filter((entry) => edgeMatchesTarget(entry, target)),
      middles: (edge.middles || []).filter((entry) => edgeMatchesTarget(entry, target)),
    },
    proof: catalog.proof,
  }
}

export async function fetchEventIntelligenceBundle() {
  const [movement, edge] = await Promise.all([
    fetchMovementBundle(),
    fetchBoardArtifact('crossbook_edge.json'),
  ])
  return { movement, edge }
}

export async function fetchOfferProvenance(pid) {
  const index = Number(pid)
  if (!Number.isInteger(index) || index < 0) return null
  provenancePromise ||= fetchBoardArtifact('prov_game-lines.json')
  const provenance = await provenancePromise
  return Array.isArray(provenance) ? provenance[index] || null : null
}

export function buildOfferDeepLink(row) {
  const link = row?.dl
  if (typeof link === 'string' && /^https?:\/\//i.test(link)) return link
  if (!link || typeof link !== 'object') return null
  if (typeof link.url === 'string' && /^https?:\/\//i.test(link.url)) return link.url
  if (link.book === 'DraftKings' && link.ev && link.sel) {
    const slug = String(link.name || 'event').toLowerCase().replace(/ @ /g, '-@-').replace(/[^a-z0-9@]+/g, '-').replace(/^-|-$/g, '')
    return `https://sportsbook.draftkings.com/event/${slug}/${encodeURIComponent(link.ev)}?outcomes=${encodeURIComponent(link.sel)}`
  }
  if (link.book === 'FanDuel' && link.mkt && link.sel) {
    return `https://sportsbook.fanduel.com/addToBetslip?marketId[0]=${encodeURIComponent(link.mkt)}&selectionId[0]=${encodeURIComponent(link.sel)}`
  }
  return null
}

export function proofPageForBook(book) {
  return `https://abet-scraper-proof-portal.vercel.app/${encodeURIComponent(String(book || '').toLowerCase())}-proof`
}
